import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner, MapPin, MapTrifold, WarningCircle, ArrowRight } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScoreBadge } from "@/components/ScoreBadge";
import CoverageMap from "@/components/CoverageMap";
import { toast } from "sonner";

const STATE_ABBR_TO_NAME = {
  SD: "South Dakota", ND: "North Dakota", MN: "Minnesota", IA: "Iowa",
  NE: "Nebraska", KS: "Kansas", MO: "Missouri", WI: "Wisconsin",
  IL: "Illinois", IN: "Indiana", MI: "Michigan", OH: "Ohio",
};

const REAL_INDUSTRIES = [
  "Auto Repair", "Restaurants", "Dental", "Legal", "Real Estate",
  "Healthcare", "Salons & Spas", "Fitness & Gyms", "Retail",
  "Plumbing", "HVAC", "Electrical", "Roofing", "Landscaping", "Construction",
];

export default function Discover() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [industry, setIndustry] = useState("Auto Repair");
  const [city, setCity] = useState("Sioux Falls");
  const [state, setState] = useState("South Dakota");
  const [count, setCount] = useState(25);
  const [wideSearch, setWideSearch] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [hideDuplicates, setHideDuplicates] = useState(false);
  const [resultMessage, setResultMessage] = useState("");

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data));
  }, []);

  const run = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setResults([]);
    setDuplicates([]);
    setResultMessage("");
    try {
      const { data } = await api.post("/discover/real", { industry, city, state, count, dedupe: true, wide_search: wideSearch });
      setResults(data.leads || []);
      setDuplicates(data.duplicates || []);
      setResultMessage(data.message || "");
      const dupes = data.skipped_duplicates || 0;
      const n = data.leads?.length || 0;
      if (n > 0 || dupes > 0) {
        const parts = [`Added ${n} new`];
        if (dupes > 0) parts.push(`${dupes} duplicate${dupes === 1 ? "" : "s"} flagged`);
        if (data.cache_hit) parts.push("from cache");
        toast.success(`${industry} in ${city}: ${parts.join(" · ")}.`);
      } else if (data.message) {
        toast.message("No results", { description: data.message });
      }
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      const isBusy = err?.response?.status === 503;
      toast.error(isBusy ? "OpenStreetMap momentarily busy" : "Search failed", {
        description: msg,
        duration: 10000,
        action: {
          label: "Retry",
          onClick: () => run(),
        },
      });
      setResultMessage(msg + (isBusy ? " · Click Retry above or below." : ""));
    } finally {
      setLoading(false);
    }
  };

  const canRetry = !!resultMessage && !loading && results.length === 0 && duplicates.length === 0;

  // Combined feed: new leads first, then duplicates (unless hidden)
  const combinedFeed = hideDuplicates ? results : [...results, ...duplicates];

  return (
    <AppShell>
      <PageHeader
        eyebrow="Real Lead Discovery"
        title="Find real Midwest businesses"
        subtitle="Search real local businesses across all 12 Midwest states — free, no API key. Every result is a real business with a real phone, website, and address."
        testid="discover-header"
      />

      <div className="px-4 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4">
          <form
            onSubmit={run}
            data-testid="discover-form"
            className={`bg-zinc-900/60 border border-zinc-800 rounded-sm p-5 space-y-4 ${loading ? "ai-beam" : ""}`}
          >
            <div className="flex items-center gap-2 px-3 py-2 bg-orange-600/10 border border-orange-600/30 rounded-sm">
              <MapTrifold size={14} weight="fill" className="text-orange-500" />
              <div className="text-[11px] font-mono uppercase tracking-wider text-orange-300">
                Real businesses · OpenStreetMap
              </div>
            </div>

            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Industry</Label>
              <Select value={industry} onValueChange={setIndustry}>
                <SelectTrigger data-testid="discover-industry-select" className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  {REAL_INDUSTRIES.map((i) => (
                    <SelectItem key={i} value={i}>{i}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[10px] font-mono text-zinc-600 mt-1">
                Best US coverage: Auto Repair, Restaurants, Dental, Legal, Real Estate.
              </div>
            </div>

            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">City</Label>
              <Input
                data-testid="discover-city-input"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="e.g. Sioux Falls"
                className="mt-1.5 bg-zinc-950 border-zinc-800 focus-visible:border-orange-500"
              />
              <div className="mt-2 max-h-36 overflow-y-auto flex flex-wrap gap-1 pr-1 pb-2 border-b border-zinc-900/60">
                {(meta?.primary_cities || []).map((c) => (
                  <button
                    type="button"
                    key={c}
                    data-testid={`city-chip-${c}`}
                    onClick={() => {
                      const [cc, st] = c.split(", ");
                      setCity(cc);
                      if (st && STATE_ABBR_TO_NAME[st]) setState(STATE_ABBR_TO_NAME[st]);
                    }}
                    className="text-[10px] font-mono px-2 py-1 bg-zinc-900 border border-zinc-800 hover:border-orange-500/50 hover:text-orange-400 text-zinc-400 rounded-sm transition-colors whitespace-nowrap"
                  >
                    {c}
                  </button>
                ))}
              </div>
              <div className="text-[10px] font-mono text-zinc-600 mt-2">
                36 Midwest cities pre-loaded · or type any US city above
              </div>
            </div>

            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">State (Midwest)</Label>
              <Select value={state} onValueChange={setState}>
                <SelectTrigger data-testid="discover-state-select" className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  {(meta?.midwest_states || ["South Dakota"]).map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">How many leads?</Label>
              <Input
                data-testid="discover-count-input"
                type="number"
                min={1}
                max={500}
                value={count}
                onChange={(e) => setCount(Number(e.target.value))}
                className="mt-1.5 bg-zinc-950 border-zinc-800"
              />
              <div className="text-[10px] font-mono text-zinc-600 mt-1">
                Up to 500 per search. Free tier is capped at 25/month.
              </div>
            </div>

            <label
              className="flex items-start gap-2 cursor-pointer select-none px-3 py-2 border border-zinc-800 rounded-sm hover:border-orange-500/40 transition-colors"
              data-testid="discover-wide-search-label"
            >
              <input
                type="checkbox"
                data-testid="discover-wide-search"
                checked={wideSearch}
                onChange={(e) => setWideSearch(e.target.checked)}
                className="mt-0.5 accent-orange-500"
              />
              <div>
                <div className="text-xs font-mono uppercase tracking-wider text-zinc-300">
                  Wide search
                </div>
                <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                  Sweep out to 80 km radius. Slower, but surfaces everything OSM has in the metro.
                </div>
              </div>
            </label>

            <Button
              type="submit"
              data-testid="discover-run-btn"
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm h-10"
            >
              {loading ? (
                <><Spinner size={16} className="mr-2 animate-spin" /> Searching real businesses…</>
              ) : (
                <><MapTrifold size={16} className="mr-2" weight="fill" /> Find real businesses</>
              )}
            </Button>

            <div className="text-[10px] font-mono text-zinc-600 pt-2 border-t border-zinc-800">
              💡 Tip: after leads appear, open any lead and click <span className="text-orange-400">Hunter</span> to auto-find the owner's real email.
            </div>
          </form>

          <div className="mt-4">
            <CoverageMap
              onPick={(pickedCity, pickedState, pickedIndustry) => {
                setCity(pickedCity);
                setState(pickedState);
                if (pickedIndustry && REAL_INDUSTRIES.includes(pickedIndustry)) {
                  setIndustry(pickedIndustry);
                }
                toast.success(`Loaded ${pickedCity}, ${pickedState}${pickedIndustry ? ` · ${pickedIndustry}` : ""}`);
              }}
            />
          </div>
        </div>

        <div className="lg:col-span-8">
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-sm min-h-[300px]">
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between gap-3">
              <div className="font-display font-medium text-zinc-100">Real businesses found</div>
              <div className="flex items-center gap-3">
                {duplicates.length > 0 && (
                  <label
                    className="flex items-center gap-1.5 text-[11px] font-mono text-zinc-400 cursor-pointer select-none"
                    data-testid="discover-hide-duplicates-label"
                  >
                    <input
                      type="checkbox"
                      data-testid="discover-hide-duplicates"
                      checked={hideDuplicates}
                      onChange={(e) => setHideDuplicates(e.target.checked)}
                      className="accent-orange-500"
                    />
                    Hide duplicates
                  </label>
                )}
                <div className="font-mono text-xs text-zinc-500" data-testid="discover-results-count">
                  {results.length} new{duplicates.length > 0 ? ` · ${duplicates.length} dup` : ""}
                </div>
              </div>
            </div>
            {combinedFeed.length === 0 ? (
              <div className="px-6 py-16 text-center" data-testid="discover-empty">
                <MapPin size={32} weight="duotone" className="mx-auto text-zinc-700 mb-3" />
                <div className="text-sm text-zinc-400">
                  {resultMessage || "Pick a city and industry on the left, then click Find."}
                </div>
                <div className="text-xs text-zinc-600 mt-1">New leads appear here and in your pipeline as "New Lead".</div>
                {canRetry && (
                  <Button
                    type="button"
                    onClick={run}
                    data-testid="discover-retry-btn"
                    className="mt-4 bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm"
                  >
                    <MapTrifold size={14} className="mr-2" weight="fill" /> Retry search
                  </Button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-zinc-800/60">
                {combinedFeed.map((l, idx) => {
                  const isDup = !!l.is_duplicate;
                  const key = isDup ? `dup-${l.existing_lead_id || idx}` : l.id;
                  return (
                    <div
                      key={key}
                      data-testid={isDup ? `discover-duplicate-${l.existing_lead_id}` : `discover-result-${l.id}`}
                      data-duplicate={isDup ? "true" : "false"}
                      onClick={() => navigate(`/leads?lead=${isDup ? l.existing_lead_id : l.id}`)}
                      className={`px-4 py-3 hover:bg-zinc-800/30 cursor-pointer transition-colors ${
                        isDup ? "bg-amber-500/[0.04] border-l-2 border-l-amber-500/60" : ""
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="text-sm text-zinc-100 font-medium truncate">{l.business_name}</div>
                            {isDup && (
                              <span
                                data-testid="duplicate-pill"
                                className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-300"
                              >
                                <WarningCircle size={10} weight="fill" /> Already in pipeline
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-zinc-500 font-mono mt-0.5">
                            {l.industry} · {l.city}{l.state ? `, ${l.state}` : ""}
                          </div>
                          <div className="text-[11px] text-zinc-400 mt-1 font-mono flex flex-wrap gap-x-3 gap-y-0.5">
                            {l.phone && <span>📞 {l.phone}</span>}
                            {l.website && <span className="truncate max-w-[260px]">🌐 {l.website.replace(/^https?:\/\//, "")}</span>}
                            {l.email && <span>✉️ {l.email}</span>}
                          </div>
                          {l.notes && (
                            <div className="text-xs text-zinc-500 mt-1 line-clamp-1">{l.notes}</div>
                          )}
                        </div>
                        {isDup ? (
                          <span
                            className="inline-flex items-center gap-1 text-[11px] font-mono text-amber-300 shrink-0"
                            data-testid="view-existing-link"
                          >
                            View existing <ArrowRight size={11} weight="bold" />
                          </span>
                        ) : (
                          <ScoreBadge score={l.lead_score} />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
